import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  getCurrentUser,
  login as loginRequest,
  logout as logoutRequest,
  type CurrentUser,
} from '@/services/authService';
import {
  onSessionRevoked,
  setSessionBelievedActive,
  type SessionRevocation,
} from '@/services/apiClient';
import { SessionNotice } from '@/components/sessionNotice/sessionNotice';
import { clearDraft } from '@/services/listingDraft';
import { shouldRevalidate } from './sessionFreshness';
import { tr } from '@/i18n/tr';

export type AuthStatus = 'loading' | 'guest' | 'authed';

interface AuthContextValue {
  status: AuthStatus;
  user: CurrentUser | null;
  login: (email: string, password: string) => Promise<CurrentUser>;
  logout: () => Promise<void>;
  /**
   * Re-probe /auth/me and update the cached user (e.g. after a profile edit).
   * Returns what it found, so a caller that has just had a session issued
   * server-side can branch on whether it actually holds.
   */
  refresh: () => Promise<CurrentUser | null>;
  /**
   * Re-probe only if the cached answer has gone stale, so opening a protected
   * screen on a tab left open for hours discovers a dead session before the
   * user is shown a form they cannot submit. Cheap to call on every mount.
   */
  revalidate: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Probes /auth/me once at boot; login/logout keep the session state in sync. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ status: AuthStatus; user: CurrentUser | null }>({
    status: 'loading',
    user: null,
  });

  const [revocation, setRevocation] = useState<SessionRevocation | null>(null);

  // The revocation subscription is registered once, so it cannot close over the
  // current user. This ref is how it reaches them without re-subscribing on
  // every login (which would drop and re-add the listener mid-flight).
  const userRef = useRef<CurrentUser | null>(null);
  userRef.current = state.user;

  // The api client cannot tell a lapsed session from a guest browsing a guarded
  // route — both are `missing_token` — so it is told which one this is.
  setSessionBelievedActive(state.user !== null);

  /** When /auth/me last answered, so a burst of navigation costs one probe. */
  const lastCheckedRef = useRef<number | null>(null);
  const revalidateRef = useRef<() => void>(() => {});

  useEffect(() => {
    let alive = true;
    getCurrentUser()
      .then((user) => {
        if (alive) setState(user ? { status: 'authed', user } : { status: 'guest', user: null });
      })
      .catch(() => {
        // network/server failure at boot — the app is browsable as guest either way
        if (alive) setState({ status: 'guest', user: null });
      });
    return () => {
      alive = false;
    };
  }, []);

  /**
   * An admin can end this session at any moment; standing is checked on every
   * request, so the first call after the ban 401s and the server has already
   * cleared the cookie. Drop to guest here so the UI stops pretending, and show
   * the user why — otherwise every action just fails with a generic error.
   */
  useEffect(
    () =>
      onSessionRevoked((next) => {
        // The draft belongs to the session that made it, exactly as in logout().
        // Read the id from a ref rather than inside the state updater: an updater
        // must stay pure, and StrictMode invokes it twice in development.
        const current = userRef.current;
        // A ban or a vanished account ends the session for good, and the draft
        // must not outlive it on a shared device. An EXPIRY does not: the same
        // person is about to sign back in, and the half-filled listing they lost
        // is the whole reason this notice exists. Wiping it here would delete
        // exactly what the re-login is meant to hand back.
        if (current && next.reason !== 'token_expired') clearDraft(String(current.id));
        setState({ status: 'guest', user: null });
        // Keep the FIRST revocation: several in-flight requests can 401 at once,
        // and the later ones would otherwise overwrite the banReason with a
        // repeat of the same event.
        setRevocation((prev) => prev ?? next);
      }),
    []
  );

  const revocationLines = (() => {
    if (!revocation) return [];
    // An expiry is not a punishment and must not read like one: no ban reason,
    // and nothing about listings being taken down, because none were.
    if (revocation.reason === 'token_expired') {
      return [tr.sessionRevoked.expired, tr.sessionRevoked.expiredDraft];
    }
    const lead =
      revocation.reason === 'account_banned' ? tr.sessionRevoked.banned : tr.sessionRevoked.missing;

    return [
      lead,
      ...(revocation.banReason ? [tr.sessionRevoked.reason(revocation.banReason)] : []),
      // A vanished account has no listings to explain the fate of.
      ...(revocation.reason === 'account_missing' ? [] : [tr.sessionRevoked.listings]),
    ];
  })();

  const login = async (email: string, password: string) => {
    const user = await loginRequest(email, password);
    setState({ status: 'authed', user });
    return user;
  };

  const logout = async () => {
    // Read the user before the state drops — this is the only place the id needed
    // to build the draft key is guaranteed to still be available, and the only
    // path every logout surface goes through.
    const current = state.user;
    try {
      await logoutRequest();
    } catch {
      // cookie is 1h max — dropping local state is the safe outcome either way
    }
    // Shared devices: a listing draft must not outlive the session that made it.
    if (current) clearDraft(String(current.id));
    setState({ status: 'guest', user: null });
  };

  const refresh = async () => {
    // Called after a deliberate change (profile edit, a session just issued), so
    // a session that has lapsed underneath it is worth saying out loud.
    const user = await getCurrentUser({ announceExpiry: true });
    lastCheckedRef.current = Date.now();
    setState(user ? { status: 'authed', user } : { status: 'guest', user: null });
    return user;
  };

  /**
   * Throttled re-probe. A session found to have ended drops the UI to guest AND
   * says so: the screen's guard then moves the user to login, and arriving there
   * with no explanation is the confusion this exists to remove.
   */
  const revalidate = () => {
    if (userRef.current === null) return; // nothing cached to go stale
    if (!shouldRevalidate(lastCheckedRef.current, Date.now())) return;
    lastCheckedRef.current = Date.now(); // claim the slot before awaiting
    void getCurrentUser({ announceExpiry: true })
      .then((user) => {
        setState(user ? { status: 'authed', user } : { status: 'guest', user: null });
      })
      .catch(() => {
        // Offline or the server is down. Neither proves the session ended, and
        // signing someone out because their train went into a tunnel is worse
        // than letting the next real request fail.
      });
  };

  /**
   * Returning to a tab is the moment a stale session is most likely, and the
   * moment before the user acts on it. `visibilitychange` covers the phone case
   * (app switched away and back); `focus` covers a desktop window regaining it.
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') revalidateRef.current();
    };
    const onFocus = () => revalidateRef.current();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Same reason as userRef: the listeners are registered once and must not
  // close over a stale copy of revalidate.
  revalidateRef.current = revalidate;

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refresh, revalidate }}>
      {children}
      <SessionNotice
        open={revocation !== null}
        title={
          // "Sonlandırıldı" reads as something an admin did to you. An expiry is
          // nobody's decision and must not be worded like a sanction.
          revocation?.reason === 'token_expired'
            ? tr.sessionRevoked.expiredTitle
            : tr.sessionRevoked.title
        }
        lines={revocationLines}
        dismissLabel={tr.sessionRevoked.dismiss}
        onDismiss={() => setRevocation(null)}
      />
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
